import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

interface ConfirmEmailProps {
  email?: string;
}

const ConfirmEmail = ({ email }: ConfirmEmailProps) => {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-primary/10 p-3">
              <Mail className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">Check Your Email</CardTitle>
          <CardDescription>
            We've sent a confirmation link to verify your account
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription className="text-sm">
              {email ? (
                <>
                  We sent an email to <strong>{email}</strong>. Click the link in the email to confirm your account and complete setup.
                </>
              ) : (
                "Check your inbox for a confirmation email. Click the link to verify your account and complete setup."
              )}
            </AlertDescription>
          </Alert>

          <div className="space-y-3 text-sm text-muted-foreground">
            <p className="font-medium">What's next?</p>
            <ol className="list-decimal list-inside space-y-2 ml-2">
              <li>Open the confirmation email</li>
              <li>Click the verification link</li>
              <li>Choose your plan based on your form count</li>
              <li>Start backing up your forms!</li>
            </ol>
          </div>

          <div className="pt-4 space-y-3">
            <Alert className="bg-muted">
              <AlertDescription className="text-xs">
                <strong>Can't find the email?</strong>
                <ul className="mt-2 space-y-1 ml-4 list-disc">
                  <li>Check your spam/junk folder</li>
                  <li>Make sure you entered the correct email</li>
                  <li>Wait a few minutes and refresh your inbox</li>
                </ul>
              </AlertDescription>
            </Alert>

            {process.env.NODE_ENV === 'development' && (
              <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                <AlertDescription className="text-xs text-blue-900 dark:text-blue-100">
                  <strong>Local Development:</strong> Check Inbucket at{' '}
                  <a
                    href="http://localhost:54324"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium"
                  >
                    localhost:54324
                  </a>
                  {' '}for your confirmation email.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-center pt-2">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/auth" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Sign In
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfirmEmail;
